// Alliance profile (spec §3.2, P21): what everyone in the galaxy may see, plus Apply.
import { RANK_ORDER } from '@stars-and-ships/api-types';
import { useState } from 'react';
import { Navigate, useOutletContext, useParams } from 'react-router';
import { ApiRequestError } from '../api/http';
import { useAlliance, useMyAllianceInvites } from '../api/queries';
import { ApplyForm } from '../components/alliance/ApplyForm';
import { Emblem } from '../components/alliance/Emblem';
import { MyInvites } from '../components/alliance/NoAllianceView';
import { RANK_LABELS } from '../components/alliance/shared';
import type { GameContext } from '../components/GameLayout';
import { PlayerLink } from '../components/ProfileLinks';
import { Button, EmptyState, ErrorNotice, Page, Panel, Spinner, Stat } from '../components/ui';

export function AllianceProfileScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const { allianceId } = useParams();
  const alliance = useAlliance(galaxyId, allianceId);
  const [applying, setApplying] = useState(false);
  const myInvites = useMyAllianceInvites(galaxyId, !me?.alliance);
  // Your own alliance has its full screen.
  if (me?.alliance && me.alliance.id === allianceId) {
    return <Navigate to={`/g/${galaxyId}/alliance`} replace />;
  }
  if (alliance.error) {
    return (
      <Page width="narrow">
        {alliance.error instanceof ApiRequestError && alliance.error.status === 404 ? (
          <EmptyState>Alliance not found.</EmptyState>
        ) : (
          <ErrorNotice error={alliance.error} />
        )}
      </Page>
    );
  }
  if (!alliance.data) return <Spinner />;
  const a = alliance.data;
  const members = [...a.members].sort(
    (x, y) => RANK_ORDER[y.rank] - RANK_ORDER[x.rank] || x.displayName.localeCompare(y.displayName)
  );
  // A pending invite or application for this alliance replaces Apply (answer it instead).
  const pending = (myInvites.data?.items ?? []).some((i) => i.alliance.id === a.id);
  const canApply = !me?.alliance && !pending && a.openRecruitment && a.memberCount < a.maxMembers;
  return (
    <Page width="narrow">
      <header className="flex items-center gap-3" data-alliance-profile={a.tag}>
        <Emblem emblem={a.emblem} tag={a.tag} size="lg" />
        <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
          {a.name} <span className="text-info">[{a.tag}]</span>
        </h1>
      </header>
      {me && !me.alliance ? <MyInvites galaxyId={galaxyId} allianceId={a.id} /> : null}
      <Panel>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Members" value={`${a.memberCount}/${a.maxMembers}`} />
          <Stat label="Systems" value={a.systemCount} />
          <Stat label="Points" value={<span data-points>{a.points}</span>} />
          <Stat label="Founded" value={new Date(a.createdAt).toLocaleDateString()} />
          <Stat label="Recruitment" value={a.openRecruitment ? 'Open' : 'Invitation only'} />
        </div>
        {a.description ? <p className="mt-3 text-sm text-muted">{a.description}</p> : null}
        {canApply && !applying ? (
          <Button className="mt-3" onClick={() => setApplying(true)}>
            Apply
          </Button>
        ) : null}
        {applying ? (
          <ApplyForm galaxyId={galaxyId} allianceId={a.id} onDone={() => setApplying(false)} />
        ) : null}
      </Panel>
      <Panel title="Members">
        <ul className="space-y-2">
          {members.map((m) => (
            <li key={m.playerId} className="flex flex-wrap justify-between gap-2 text-sm">
              <PlayerLink
                galaxyId={galaxyId}
                playerId={m.playerId}
                name={m.displayName}
                className="text-text"
              />
              <span className="text-xs text-muted">
                {RANK_LABELS[m.rank]} · {m.points} pts · joined{' '}
                {new Date(m.joinedAt).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </Page>
  );
}
